import { NewsletterSection } from '@/components/home/NewsletterSection';
import { ServiceTargetSection, TargetAudience } from '@/components/prestations/ServiceTargetSection';
import { UseCaseCard } from '@/components/prestations/UseCaseCard';
import { MethodologyTimeline } from '@/components/prestations/MethodologyTimeline';
import {
  FileText,
  Search,
  Mail,
  Presentation,
  Building2,
  Briefcase,
  GraduationCap,
  BarChart3,
  Users,
  Edit,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { SEOHead } from '@/components/SEO/SEOHead';

export function DossiersTechniquesPage() {
  const { t, i18n } = useTranslation('dossiersTechniques');
  const { locale } = useParams<{ locale: string }>();
  const currentLocale = locale || i18n.language || 'fr';

  const useCases = [
    {
      client: t('useCases.cases.nitrogen.client'),
      context: t('useCases.cases.nitrogen.context'),
      challenge: t('useCases.cases.nitrogen.challenge'),
      solution: t('useCases.cases.nitrogen.solution'),
      result: t('useCases.cases.nitrogen.result'),
      image: 'https://vztdmeixujtqwstyajzs.supabase.co/storage/v1/object/public/articles-images/og/le-raisonnement-de-la-fertilisation-azotee-par-les-outils-numeriques-une-amourette-assez-fragile.png',
      tags: ['Infographie', 'Dossier', 'Grandes Cultures', 'Fertilisation'],
      articleSlug: 'le-raisonnement-de-la-fertilisation-azotee-par-les-outils-numeriques-une-amourette-assez-fragile',
      articleImage: 'https://vztdmeixujtqwstyajzs.supabase.co/storage/v1/object/public/articles-images/og/le-raisonnement-de-la-fertilisation-azotee-par-les-outils-numeriques-une-amourette-assez-fragile.png',
      articleTitle: 'Le raisonnement de la fertilisation azotée par les outils numériques : une amourette assez fragile',
    },
    {
      client: t('useCases.cases.bioagressors.client'),
      context: t('useCases.cases.bioagressors.context'),
      challenge: t('useCases.cases.bioagressors.challenge'),
      solution: t('useCases.cases.bioagressors.solution'),
      result: t('useCases.cases.bioagressors.result'),
      image: 'https://vztdmeixujtqwstyajzs.supabase.co/storage/v1/object/public/articles-images/og/le-numerique-au-service-de-la-gestion-des-bioagresseurs-en-agriculture.png',
      tags: ['Présentation', 'Blog', 'Bioagresseurs', 'Protection'],
      articleSlug: 'le-numerique-au-service-de-la-gestion-des-bioagresseurs-en-agriculture',
      articleImage: 'https://vztdmeixujtqwstyajzs.supabase.co/storage/v1/object/public/articles-images/og/le-numerique-au-service-de-la-gestion-des-bioagresseurs-en-agriculture.png',
      articleTitle: 'Le numérique au service de la gestion des bioagresseurs en agriculture',
    },
    {
      client: t('useCases.cases.risks.client'),
      context: t('useCases.cases.risks.context'),
      challenge: t('useCases.cases.risks.challenge'),
      solution: t('useCases.cases.risks.solution'),
      result: t('useCases.cases.risks.result'),
      image: '/placeholder-risk-management.jpg',
      tags: ['Veille', 'Cartographie', 'Risques', 'OAD'],
    },
  ];

  const targetAudiences: TargetAudience[] = [
    {
      icon: Building2,
      title: t('targetSection.audiences.institutes.title'),
      description: t('targetSection.audiences.institutes.description'),
      badge: t('targetSection.audiences.institutes.badge'),
    },
    {
      icon: Briefcase,
      title: t('targetSection.audiences.consulting.title'),
      description: t('targetSection.audiences.consulting.description'),
      badge: t('targetSection.audiences.consulting.badge'),
    },
    {
      icon: GraduationCap,
      title: t('targetSection.audiences.research.title'),
      description: t('targetSection.audiences.research.description'),
      badge: t('targetSection.audiences.research.badge'),
    },
  ];

  const methodologySteps = [
    {
      number: '1',
      title: t('methodology.steps.framing.title'),
      description: t('methodology.steps.framing.description'),
      icon: Users,
    },
    {
      number: '2',
      title: t('methodology.steps.research.title'),
      description: t('methodology.steps.research.description'),
      icon: Search,
    },
    {
      number: '3',
      title: t('methodology.steps.analysis.title'),
      description: t('methodology.steps.analysis.description'),
      icon: BarChart3,
    },
    {
      number: '4',
      title: t('methodology.steps.writing.title'),
      description: t('methodology.steps.writing.description'),
      icon: Edit,
    },
    {
      number: '5',
      title: t('methodology.steps.delivery.title'),
      description: t('methodology.steps.delivery.description'),
      icon: Presentation,
    },
  ];
  return (
    <div>
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#55BC8C] to-[#4a9f77] text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {/* Breadcrumb */}
            <div className="text-sm mb-6 opacity-90">
              <a href={`/${currentLocale}`} className="hover:underline">
                {t('breadcrumb.home')}
              </a>
              {' > '}
              <a href={`/${currentLocale}/prestations`} className="hover:underline">
                {t('breadcrumb.prestations')}
              </a>
              {' > '}
              <span>{t('breadcrumb.current')}</span>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Texte */}
              <div>
                <h1 className="text-4xl md:text-6xl font-heading font-extrabold mb-6">
                  {t('hero.title')}
                </h1>
                <p className="text-xl mb-8 opacity-95">
                  {t('hero.subtitle')}
                </p>

                <div className="flex flex-col sm:flex-row gap-4">
                  <a
                    href={`/${currentLocale}/contact`}
                    className="px-8 py-4 bg-white text-[#55BC8C] rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg text-center"
                  >
                    {t('hero.ctaPrimary')}
                  </a>
                  <a
                    href="#exemples"
                    className="px-8 py-4 bg-transparent border-2 border-white rounded-lg font-semibold hover:bg-white/10 transition-colors text-center"
                  >
                    {t('hero.ctaSecondary')}
                  </a>
                </div>
              </div>

              {/* Cards formats */}
              <div className="grid grid-cols-2 gap-4">
                {/* Card 1: Entretiens */}
                <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                  <div className="flex flex-col items-center text-center">
                    <div className="w-16 h-16 rounded-lg bg-[#55BC8C]/10 flex items-center justify-center mb-3 p-3">
                      <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                        {/* Microphone professionnel */}
                        <ellipse cx="50" cy="35" rx="15" ry="22" fill="#55BC8C" opacity="0.2"/>
                        <rect x="42" y="20" width="16" height="30" fill="none" stroke="#55BC8C" strokeWidth="2.5" rx="8"/>
                        <line x1="35" y1="30" x2="35" y2="40" stroke="#55BC8C" strokeWidth="2" strokeLinecap="round"/>
                        <line x1="65" y1="30" x2="65" y2="40" stroke="#55BC8C" strokeWidth="2" strokeLinecap="round"/>
                        <path d="M 35 50 Q 35 60 50 60 Q 65 60 65 50" fill="none" stroke="#55BC8C" strokeWidth="2.5" strokeLinecap="round"/>
                        <line x1="50" y1="60" x2="50" y2="75" stroke="#55BC8C" strokeWidth="3" strokeLinecap="round"/>
                        <rect x="40" y="75" width="20" height="8" fill="#55BC8C" rx="2"/>
                        {/* Ondes sonores */}
                        <path d="M 25 35 Q 20 35 18 40" fill="none" stroke="#55BC8C" strokeWidth="2" strokeLinecap="round" opacity="0.5"/>
                        <path d="M 75 35 Q 80 35 82 40" fill="none" stroke="#55BC8C" strokeWidth="2" strokeLinecap="round" opacity="0.5"/>
                      </svg>
                    </div>
                    <div className="font-bold text-aspexit-black text-sm">Entretiens</div>
                  </div>
                </div>

                {/* Card 2: Articles scientifiques */}
                <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                  <div className="flex flex-col items-center text-center">
                    <div className="w-16 h-16 rounded-lg bg-[#55BC8C]/10 flex items-center justify-center mb-3 p-3">
                      <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                        {/* Document avec graphique scientifique */}
                        <rect x="25" y="15" width="50" height="70" fill="#55BC8C" opacity="0.1" rx="3"/>
                        <rect x="25" y="15" width="50" height="70" fill="none" stroke="#55BC8C" strokeWidth="2.5" rx="3"/>
                        <path d="M 25 25 L 45 25" fill="none" stroke="#55BC8C" strokeWidth="2" strokeLinecap="round"/>
                        <line x1="30" y1="35" x2="70" y2="35" stroke="#55BC8C" strokeWidth="1.5"/>
                        <line x1="30" y1="42" x2="65" y2="42" stroke="#55BC8C" strokeWidth="1.5"/>
                        <line x1="30" y1="49" x2="68" y2="49" stroke="#55BC8C" strokeWidth="1.5"/>
                        {/* Graphique */}
                        <path d="M 32 60 L 38 55 L 45 62 L 52 50 L 58 58 L 65 52 L 68 60" fill="none" stroke="#55BC8C" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="52" cy="50" r="2" fill="#55BC8C"/>
                        {/* Badge science */}
                        <circle cx="65" cy="22" r="8" fill="#55BC8C" opacity="0.3"/>
                        <text x="65" y="26" fill="#55BC8C" fontSize="10" fontWeight="bold" textAnchor="middle">∑</text>
                      </svg>
                    </div>
                    <div className="font-bold text-aspexit-black text-sm">Articles scientifiques</div>
                  </div>
                </div>

                {/* Card 3: Rapports Techniques */}
                <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                  <div className="flex flex-col items-center text-center">
                    <div className="w-16 h-16 rounded-lg bg-[#55BC8C]/10 flex items-center justify-center mb-3 p-3">
                      <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                        {/* Dossier épais avec données */}
                        <rect x="20" y="25" width="60" height="60" fill="#55BC8C" opacity="0.15" rx="2"/>
                        <rect x="20" y="25" width="60" height="60" fill="none" stroke="#55BC8C" strokeWidth="2.5" rx="2"/>
                        <rect x="23" y="28" width="54" height="8" fill="#55BC8C" opacity="0.3"/>
                        {/* Lignes de texte */}
                        <line x1="28" y1="45" x2="72" y2="45" stroke="#55BC8C" strokeWidth="2" strokeLinecap="round"/>
                        <line x1="28" y1="52" x2="68" y2="52" stroke="#55BC8C" strokeWidth="2" strokeLinecap="round"/>
                        <line x1="28" y1="59" x2="70" y2="59" stroke="#55BC8C" strokeWidth="2" strokeLinecap="round"/>
                        <line x1="28" y1="66" x2="65" y2="66" stroke="#55BC8C" strokeWidth="2" strokeLinecap="round"/>
                        <line x1="28" y1="73" x2="72" y2="73" stroke="#55BC8C" strokeWidth="2" strokeLinecap="round"/>
                        {/* Icône engrenage technique */}
                        <circle cx="70" cy="70" r="10" fill="white" stroke="#55BC8C" strokeWidth="2"/>
                        <circle cx="70" cy="70" r="5" fill="#55BC8C"/>
                        <path d="M 70 60 L 70 63 M 70 77 L 70 80 M 60 70 L 63 70 M 77 70 L 80 70" stroke="#55BC8C" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    </div>
                    <div className="font-bold text-aspexit-black text-sm">Rapports Techniques</div>
                  </div>
                </div>

                {/* Card 4: Infographies et Visuels */}
                <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                  <div className="flex flex-col items-center text-center">
                    <div className="w-16 h-16 rounded-lg bg-[#55BC8C]/10 flex items-center justify-center mb-3 p-3">
                      <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                        {/* Palette de peintre moderne */}
                        <ellipse cx="45" cy="50" rx="30" ry="25" fill="#55BC8C" opacity="0.15"/>
                        <path d="M 20 45 Q 15 50 20 55 Q 25 60 35 60 L 55 60 Q 70 60 75 50 Q 78 40 70 32 Q 60 25 45 25 Q 30 25 20 35 Q 15 40 20 45 Z"
                              fill="none" stroke="#55BC8C" strokeWidth="2.5"/>
                        {/* Taches de couleur */}
                        <circle cx="35" cy="40" r="5" fill="#55BC8C" opacity="0.4"/>
                        <circle cx="52" cy="38" r="5" fill="#55BC8C" opacity="0.6"/>
                        <circle cx="42" cy="52" r="5" fill="#55BC8C" opacity="0.5"/>
                        {/* Pinceau */}
                        <rect x="60" y="55" width="5" height="25" fill="#55BC8C" opacity="0.3" rx="1"/>
                        <path d="M 60 55 L 58 50 L 67 50 L 65 55 Z" fill="#55BC8C"/>
                        <line x1="62.5" y1="50" x2="62.5" y2="42" stroke="#55BC8C" strokeWidth="2" strokeLinecap="round"/>
                        {/* Étoiles créatives */}
                        <circle cx="75" cy="35" r="2" fill="#55BC8C"/>
                        <circle cx="68" cy="68" r="2" fill="#55BC8C"/>
                      </svg>
                    </div>
                    <div className="font-bold text-aspexit-black text-sm">Infographies et Visuels</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pour qui */}
      <ServiceTargetSection
        title={t('targetSection.title')}
        subtitle={t('targetSection.subtitle')}
        audiences={targetAudiences}
        accentColor="green"
      />

      {/* Exemples de dossiers */}
      <section id="exemples" className="py-24 bg-aspexit-gray-light">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-heading font-extrabold text-aspexit-black mb-4 text-center">
              {t('useCases.title')}
            </h2>
            <p className="text-xl text-aspexit-gray text-center mb-16 max-w-2xl mx-auto">
              {t('useCases.subtitle')}
            </p>

            <div className="space-y-12">
              {useCases.map((useCase, index) => (
                <UseCaseCard key={index} useCase={useCase} index={index} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Méthodologie */}
      <MethodologyTimeline
        title={t('methodology.title')}
        subtitle={t('methodology.subtitle')}
        steps={methodologySteps}
        size="compact"
        accentColor="green"
      />

      {/* Pourquoi moi */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-heading font-extrabold text-aspexit-black mb-12 text-center">
              Pourquoi travailler avec moi
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              <div className="text-center">
                <div className="text-5xl font-bold text-[#55BC8C] mb-2">
                  60+
                </div>
                <p className="text-aspexit-gray">Dossiers techniques publiés</p>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold text-[#55BC8C] mb-2">
                  10+
                </div>
                <p className="text-aspexit-gray">
                  Années de veille sur le numérique agricole
                </p>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold text-[#55BC8C] mb-2">
                  100%
                </div>
                <p className="text-aspexit-gray">Sources scientifiques vérifiées</p>
              </div>
            </div>

            <div className="bg-aspexit-gray-light rounded-lg p-8">
              <h3 className="text-xl font-heading font-semibold text-aspexit-black mb-4">
                Mes atouts
              </h3>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 text-aspexit-gray">
                <li className="flex items-center gap-2">
                  <span className="text-[#55BC8C]">✓</span>
                  Double expertise : agronomie + numérique
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-[#55BC8C]">✓</span>
                  Réseau professionnel étendu
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-[#55BC8C]">✓</span>
                  Accès bases scientifiques (Scholar, WoS)
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-[#55BC8C]">✓</span>
                  Veille quotidienne depuis 10 ans
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-[#55BC8C]">✓</span>
                  Co-créateur Wiki Agri Tech
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-[#55BC8C]">✓</span>
                  Rédaction claire et structurée
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-24 bg-gradient-to-br from-[#55BC8C] to-[#4a9f77] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl font-heading font-bold mb-4">
              {t('cta.title')}
            </h2>
            <p className="text-xl mb-8 opacity-90">
              {t('cta.subtitle')}
            </p>

            <div className="flex justify-center">
              <a
                href={`/${currentLocale}/contact`}
                className="px-8 py-4 bg-white text-[#55BC8C] rounded-lg font-semibold hover:bg-aspexit-gray-light transition-colors shadow-lg inline-flex items-center justify-center gap-2"
              >
                <Mail className="w-5 h-5" />
                {t('cta.button')}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <NewsletterSection />
    </div>
  );
}
